/**
 * Stop a drawer's focus trap from pulling focus into a document the donor is
 * not in (EMB-012).
 *
 * Radix's FocusScope (inside every vaul `Drawer.Content`) watches its subtree.
 * When a node is removed while `document.activeElement` is <body>, it assumes
 * focus was lost and calls `container.focus()`. Inside ONE document that is the
 * steal `ResponsiveDrawer` already hands back. Across documents it is worse:
 * the overlay host keeps the widget's drawer open in its own iframe while the
 * donor works in the checkout iframe beside it. The widget document's
 * `activeElement` is <body> then — focus is in another frame — so any re-render
 * there (the widget redraws its copy of a question the donor just answered)
 * makes its FocusScope call `container.focus()`, and Chrome moves the donor's
 * focus out of checkout and into the widget. Measured on the merged EMB-012
 * matrix: Back lost focus with `relatedTarget` null a moment after the Tab had
 * landed it correctly, and the widget frame logged `focusin` on its drawer
 * container at the same instant.
 *
 * WHAT IS REFUSED, EXACTLY: the container's `focus()` call made by that repair,
 * and nothing else. A deliberate focus call is left alone even while the
 * document does not have focus. `DonorInfo` moving focus into the checkout when
 * it is shown (A11-003) is one such call, and refusing it would strand the donor
 * in the page behind.
 *
 * HOW THE REPAIR IS RECOGNISED: FocusScope's repair runs inside a
 * MutationObserver callback, answering removals made while nothing is focused.
 * The guard watches the same subtree. When the container's `focus()` is called,
 * the guard checks whether a removal happened while the document had no focus
 * and nothing in it was focused. That is the trap's own trigger, plus "the donor
 * is elsewhere". Observers are notified in creation order, and the container can
 * re-attach (so the guard's observer can be newer than the trap's), so the check
 * covers both orders:
 * - the guard was notified first, and holds a refusal window that closes on the
 *   next microtask;
 * - or the removals are still queued for the guard, and `takeRecords()` reads
 *   them.
 *
 * `document.hasFocus()` is true when focus is in this document or in a frame
 * nested inside it (a Stripe card field, say), so a same-document repair is not
 * refused. `ResponsiveDrawer` hands that case back itself.
 *
 * @param {HTMLElement | null} node the drawer container (`Drawer.Content`)
 * @returns {() => void} disconnects the guard and restores the element's `focus`
 */
export function guardFocusWhileDocumentUnfocused(node) {
  if (!node || typeof node.focus !== 'function' || typeof MutationObserver === 'undefined') {
    return () => {};
  }
  const doc = node.ownerDocument;
  const isRepairTrigger = (records) =>
    records.some((record) => record.removedNodes.length > 0) &&
    doc.activeElement === doc.body &&
    !doc.hasFocus();
  let refusing = false;
  // The trap calls focus() once PER removal record in the batch, so a refusal
  // must cover the rest of that batch, not just the first call. It closes on
  // the next microtask, after every observer callback of this notification.
  const refuseForThisBatch = () => {
    if (refusing) return;
    refusing = true;
    queueMicrotask(() => {
      refusing = false;
    });
  };
  const observer = new MutationObserver((records) => {
    if (isRepairTrigger(records)) refuseForThisBatch();
  });
  observer.observe(node, { childList: true, subtree: true });

  const ownFocus = node.focus;
  node.focus = function guardedFocus(...args) {
    // Either this guard's observer already saw the removals (it was notified
    // first), or they are still queued for it because the trap's observer was
    // notified first; `takeRecords()` reads that queue. Both mean this call is
    // the trap answering the removal.
    if (!refusing && isRepairTrigger(observer.takeRecords())) refuseForThisBatch();
    if (refusing) return undefined;
    return ownFocus.apply(this, args);
  };
  return () => {
    observer.disconnect();
    node.focus = ownFocus;
  };
}
